import type { SEOHandle } from "@nasa-gcn/remix-seo";
import type { MetaFunction } from "@remix-run/node";

import CtaBanner from "~/components/sections/CtaBanner";
import ProcessSteps from "~/components/sections/ProcessSteps";
import WhyChoose from "~/components/sections/WhyChoose";
import generateSitemapEntriesFor from "~/helpers/sitemapEntriesWithLocal";
import common from "~/i18n/common";
import pages from "~/i18n/pages";
import { useT } from "~/i18n/useT";
import { buildMeta, langFromParams } from "~/seo/meta";
import { breadcrumbSchema } from "~/seo/schema";

import "./about.scss";

const GALLERY = [
  "/img/about/about_img_1.webp",
  "/img/about/about_img_2.webp",
  "/img/about/about_img_3.webp",
];

export const meta: MetaFunction = ({ params }) => {
  const lang = langFromParams(params);
  const t = pages[lang].about;

  return [
    ...buildMeta({
      lang,
      path: "/about",
      title: t.metaTitle,
      description: t.metaDescription,
    }),
    {
      "script:ld+json": breadcrumbSchema(lang, [
        { name: common[lang].a11y.home, path: "/" },
        { name: common[lang].nav.about, path: "/about" },
      ]),
    },
  ];
};

export const handle: SEOHandle = generateSitemapEntriesFor(
  "/about",
  undefined,
  "monthly",
  0.7,
);

export default function AboutPage() {
  const t = useT(pages).about;
  const c = useT(common);

  return (
    <main id="main-content">
      <section className="ss-container ss-about-hero">
        <p className="ss-eyebrow ss-eyebrow--blue">{t.eyebrow}</p>
        <h1 className="ss-about-hero__title">{t.title}</h1>
        <p className="ss-about-hero__lead">{t.lead}</p>

        <div className="ss-about-hero__gallery">
          {GALLERY.map((src, index) => (
            <div key={src} className="ss-about-hero__tile">
              <img
                src={src}
                alt={t.imageAlt[index]}
                width={400}
                height={260}
                loading={index === 0 ? "eager" : "lazy"}
                decoding="async"
              />
            </div>
          ))}
        </div>
      </section>

      <section className="ss-section ss-container ss-split">
        <div className="ss-about-story__media">
          <img
            src="/img/about/about_img_9.webp"
            alt={t.storyImageAlt}
            width={560}
            height={440}
            loading="lazy"
            decoding="async"
          />
        </div>
        <div>
          <p className="ss-eyebrow">{t.storyEyebrow}</p>
          <h2 className="ss-h2 ss-about-story__title">{t.storyTitle}</h2>
          <p className="ss-about-story__body">{t.storyBody1}</p>
          <p className="ss-about-story__body">{t.storyBody2}</p>
        </div>
      </section>

      <ProcessSteps variant="center" />
      <WhyChoose />

      <CtaBanner
        title={t.cta.title}
        body={t.cta.body}
        action={c.cta.getInTouch}
      />
    </main>
  );
}
